import {OrderedSet} from 'immutable';
import {constant, concat} from '@packages/utilities/fp/utils';
import {Component, Expressions, matchExpression} from '@packages/systems/wfdl';
import {COMBINATORS} from '@packages/systems/core/constants/Selectors';
import {getStyleBlockIds} from '@packages/systems/style/style-blocks';
import type {
  RawStyleBlockServerType,
  StyleBlockVariantType,
} from '@packages/systems/core/utils/StyleTypes';
import type {RawStyleBlockType} from '@packages/systems/style-legacy/values';
import type {StyleBlockMapType} from './types';
import {Expression} from '@packages/systems/wfdl-ast';
import {parseVariantKey} from '@packages/systems/style-legacy/variants';
import {parseVariantStateKey} from '@packages/systems/style-legacy/selectors';
import {weakMemo} from '@packages/utilities/memo';

const getStyleBlockByID = (
  styleBlockId: string,
  styleBlockMap: StyleBlockMapType
): RawStyleBlockServerType | null | undefined => {
  return styleBlockMap[styleBlockId];
};

type SelectorIndex = Record<string, Set<string>>;

const buildSelectorIndex = weakMemo(
  (
    styleBlocks: (RawStyleBlockServerType | null | undefined)[]
  ): SelectorIndex => {
    const selectorIndex: SelectorIndex = {};

    const addIdToIndex = (selector: string, id: string) => {
      if (!selectorIndex[selector]) {
        selectorIndex[selector] = new Set();
      }
      selectorIndex[selector].add(id);
    };

    for (const block of styleBlocks) {
      if (block?.data.type === 'class' && block.data.sel) {
        addIdToIndex(block.data.sel, block._id);
        block.data.sel
          .split('.')
          .filter(Boolean)
          .forEach((part) => addIdToIndex(`.${part}`, block._id));
      }
    }

    return selectorIndex;
  }
);

export const getIndirectlyAppliedClassStyleBlockIds = (
  directlyAppliedStyleBlockIds: string[],
  styleBlockMap: StyleBlockMapType,
  styleBlockMapValues: (RawStyleBlockServerType | null | undefined)[]
): string[] => {
  const directlyAppliedStyleBlockIdsSet = new Set(directlyAppliedStyleBlockIds);
  const indirectlyAppliedStyleBlockIds = new Set<string>();

  // Build index of all selectors to their style block IDs
  const selectorIndex = buildSelectorIndex(styleBlockMapValues ?? []);
  const selectorAlreadyDone = new Set<string>();

  // Get all directly applied selectors
  directlyAppliedStyleBlockIds.forEach((id) => {
    const block = styleBlockMap[id];
    if (!block || block.data.type !== 'class' || !block.data.sel) return;

    // skip if already done
    if (selectorAlreadyDone.has(block.data.sel)) {
      return;
    }

    // For each directly applied selector, find all style blocks that use
    // a combination of these classes
    const parts = block.data.sel.split('.').filter(Boolean);
    for (const part of parts) {
      selectorIndex[`.${part}`]?.forEach((matchId) => {
        const matchBlock = styleBlockMap[matchId];
        if (!matchBlock?.data.sel) return;

        // Only include blocks that aren't directly applied and whose
        // selector parts are all in the directly applied set
        if (!directlyAppliedStyleBlockIdsSet.has(matchId)) {
          const matchParts = matchBlock.data.sel.split('.').filter(Boolean);
          if (
            matchParts.every((p) => {
              return Array.from(selectorIndex[`.${p}`] ?? []).some((nestedId) =>
                directlyAppliedStyleBlockIdsSet.has(nestedId)
              );
            })
          ) {
            indirectlyAppliedStyleBlockIds.add(matchId);
          }
        }
      });
    }
    selectorAlreadyDone.add(block.data.sel);
  });

  return Array.from(indirectlyAppliedStyleBlockIds);
};

const getStyleBlockIdsForExpression = (styleBlockMap: StyleBlockMapType) => {
  const styleBlockMapValues = Object.values(styleBlockMap);
  return matchExpression({
    Element: (element) => {
      const directlyAppliedStyleBlockIds = getStyleBlockIds(element);
      const indirectStyleIds = styleBlockMapValues.length
        ? getIndirectlyAppliedClassStyleBlockIds(
            directlyAppliedStyleBlockIds,
            styleBlockMap,
            styleBlockMapValues
          )
        : [];

      return Expressions.resume(
        directlyAppliedStyleBlockIds.concat(indirectStyleIds)
      );
    },
    _: constant(Expressions.resume([])),
  });
};

export const getIndirectlyAppliedNestedElementSelectorStyleBlockIds = (
  styleBlockIds: string[],
  styleBlockMap: StyleBlockMapType
) => {
  if (!Object.keys(styleBlockMap).length) return [];

  const indirectlyAppliedNestedStyleBlockIds: Array<string> = [];

  // Go thru the children of each directly applied styleBlock and evaluate whether or not it is a nested child (if so, it is an indirectly applied style)
  styleBlockIds.forEach((styleBlockId) => {
    const styleBlock = getStyleBlockByID(styleBlockId, styleBlockMap);
    if (!styleBlock) return;

    const {data} = styleBlock;

    if (Array.isArray(data.children) && data.children.length) {
      data.children.forEach((childId) => {
        const child = getStyleBlockByID(childId, styleBlockMap);
        if (!child) return;
        if (
          child.data.comb === COMBINATORS.DESCENDANT &&
          child.data.type === 'tag'
        ) {
          indirectlyAppliedNestedStyleBlockIds.push(child._id);
        }
      });
    }
  });

  return indirectlyAppliedNestedStyleBlockIds;
};

export const getAllStyleBlockIdsInExpression = (
  styleBlockMap: StyleBlockMapType
) =>
  Expressions.everything(concat)(getStyleBlockIdsForExpression(styleBlockMap));

export const getTagStyleBlockIdsWithStyleVariants = (
  variants: Component.Variants | undefined,
  styleBlockMap: StyleBlockMapType
): string[] => {
  if (!variants) return [];

  const styleIdsToInclude = new Set<string>();
  const styleBlockMapEntries = Object.entries(styleBlockMap);
  const variantFieldIds = new Set<string>(Object.keys(variants));

  for (const [id, block] of styleBlockMapEntries) {
    if (!block?.data || !block.data.variants || block.data.type !== 'tag')
      continue;

    const blockContainsVariantFieldId = Object.keys(block.data.variants).some(
      (key) => {
        const {variantState} = parseVariantKey(key);
        if (!variantState) return false;

        const [parsedVariantFieldId] = parseVariantStateKey(variantState);

        return variantFieldIds.has(parsedVariantFieldId);
      }
    );

    if (blockContainsVariantFieldId) {
      styleIdsToInclude.add(id);
    }
  }

  return [...styleIdsToInclude];
};

const getAllStyleBlocksIdsToInclude = (
  styleIdsOnElementPreset: Array<string>,
  styleBlockMap: StyleBlockMapType
): OrderedSet<string> => {
  const styleIdsToInclude = new Set(styleIdsOnElementPreset);
  const indirectlyAppliedNestedElementSelectorStyleBlockIds =
    getIndirectlyAppliedNestedElementSelectorStyleBlockIds(
      styleIdsOnElementPreset,
      styleBlockMap
    );

  indirectlyAppliedNestedElementSelectorStyleBlockIds.forEach((id) =>
    styleIdsToInclude.add(id)
  );

  const styleBlockOrderMap = Object.entries(styleBlockMap).reduce<
    Record<string, number>
  >((acc, [id], index) => {
    acc[id] = index;
    return acc;
  }, {});

  const sortedStyleBlockIds = Array.from(styleIdsToInclude).sort(
    (a, b) => (styleBlockOrderMap[a] ?? 0) - (styleBlockOrderMap[b] ?? 0)
  );

  return OrderedSet(sortedStyleBlockIds);
};

export const getAllStyleBlocksToInclude = (
  styleIdsOnElementPreset: Array<string>,
  styleBlockMap: StyleBlockMapType
): Array<RawStyleBlockType> => {
  // if there are no items in the styleBlockMap, there are no styleBlocks to gather, thus return an empty array
  if (!Object.keys(styleBlockMap).length) return [];
  const styleBlocks: Array<RawStyleBlockType> = [];
  const styleIdsToInclude = getAllStyleBlocksIdsToInclude(
    styleIdsOnElementPreset,
    styleBlockMap
  );

  styleIdsToInclude.forEach((_id: string) => {
    const block = getStyleBlockByID(_id, styleBlockMap);
    if (!block) return;

    const styleBlock: RawStyleBlockType = {
      _id,
      type: block.data.type,
      name: block.data.name,
      variants: block.data.variants,
    };

    // NOTE: exclude tag blocks with descendant selectors from this branch
    // as these will contain `styleLess` data that we want included in the library
    // and cannot contain variant style data anyways because descendant tag selectors
    // cannot be created/edited while inside of a component.
    if (
      block.data.type === 'tag' &&
      block.data.comb !== COMBINATORS.DESCENDANT
    ) {
      // When a tag-based style block is being included in dependencies
      // exclude any `styleBlock.variants` entries whose key does not contain a `VariantStateKey`.
      styleBlock.variants = block.data.variants
        ? Object.entries(block.data.variants).reduce(
            (acc, [key, value]) => {
              if (parseVariantKey(key).variantState) {
                acc[key] = value;
              }
              return acc;
            },
            {} as {
              [name: string]: StyleBlockVariantType;
            }
          )
        : undefined;
    } else {
      // NOTE: In all other cases, including when the style block is a descendant tag selector,
      // include the block's root `styleLess` data in the style block being exported by the library.
      styleBlock.styleLess = block.data.styleLess;
      styleBlock.variants = block.data.variants;
    }

    // comb is an optional field and should only be added to the StyleBlock if data for it exists
    if (block.data.comb) {
      styleBlock.comb = block.data.comb;
    }

    // children is an optional field and a styleBlock's child should only be included if the child's Id is in the styleIdsToInclude set
    if (Array.isArray(block.data.children) && block.data.children.length) {
      styleBlock.children = block.data.children.filter((child) =>
        styleIdsToInclude.has(child)
      );
    }
    styleBlocks.push(styleBlock);
  });

  return styleBlocks;
};

/**
 * Collects all style blocks from a component render expression.
 */
export const getStyleBlockIdsFromComponentRender = (
  render: Expression,
  styleBlockMap: StyleBlockMapType
): string[] => {
  const styleIdsOnElementPreset =
    getAllStyleBlockIdsInExpression(styleBlockMap)(render);
  const styleIdsToInclude = getAllStyleBlocksIdsToInclude(
    styleIdsOnElementPreset,
    styleBlockMap
  );
  return Array.from(styleIdsToInclude);
};

export function generateStyleBlockMap(
  styleBlocks: Array<RawStyleBlockServerType>
): StyleBlockMapType {
  return styleBlocks.reduce<Record<string, RawStyleBlockServerType>>(
    (memo, block) => {
      memo[block._id] = block;
      return memo;
    },
    {}
  );
}
